<script setup>
import {computed, ref, watch} from 'vue'
import {useStore} from 'vuex'
import {useRouter, useRoute} from 'vue-router'
import {useWindowSize} from '@vueuse/core'
import {Ellipsis, Folder, Folders, Pin, UserRoundPlus} from 'lucide-vue-next'

import {Button} from '@/components/ui/button'
import ProjectActionsMenu from './ProjectActionsMenu.vue'
import {useProjectPins} from '@/composables/useProjectPins'
import {buildStaticMapUrl} from '@/utils/mapbox/staticMap'
import {formatTimeFromNow} from '@/utils/datetime'

const props = defineProps({
  project: Object,
  parentName: String,
  boundary: Object,
  joinable: Boolean
})
const emit = defineEmits(['join'])

const store = useStore()
const router = useRouter()
const route = useRoute()

const settings = computed(() => store.getters['user/getUserSettings'])
const isSelected = computed(() => parseInt(route.params.project_id) === props.project.project_id)

const {width: windowWidth} = useWindowSize()
const isTouchLayout = computed(() => windowWidth.value < 1024)

const previewFailed = ref(false)
const previewUrl = computed(() => props.boundary ? buildStaticMapUrl(props.boundary) : null)
const showPreview = computed(() => !!previewUrl.value && !previewFailed.value)

watch(previewUrl, () => {
  previewFailed.value = false
})

const lastActivity = computed(() => {
  if (!props.project.last_activity) return ''
  return formatTimeFromNow(props.project.last_activity, settings.value?.tz)
})

const {isPinned, togglePin} = useProjectPins()
const pinned = computed(() => isPinned(props.project.project_id))

const openProject = () => {
  if (props.joinable) {
    emit('join', props.project)
    return
  }
  router.push({
    name: 'ProjectHome',
    params: { org_id: props.project.organization_id, project_id: props.project.project_id }
  })
}
</script>

<template>
  <div
    class="group cursor-pointer rounded-lg border bg-card p-2 transition-colors hover:border-primary"
    :class="isSelected ? 'border-primary ring-1 ring-primary' : ''"
    @click="openProject"
  >
    <div class="relative aspect-video w-full overflow-hidden rounded-md bg-muted">
      <img
        v-if="showPreview"
        :src="previewUrl"
        :alt="`Map preview of ${project.project_name}`"
        class="w-full h-full object-cover"
        loading="lazy"
        @error="previewFailed = true"
      />
      <div v-else class="w-full h-full flex items-center justify-center">
        <span v-if="project.emoji" class="text-2xl leading-none">{{ project.emoji }}</span>
        <Folders v-else-if="project.children?.length" class="w-6 h-6 text-muted-foreground" />
        <Folder v-else class="w-6 h-6 text-muted-foreground" />
      </div>

      <div
        v-if="joinable"
        class="absolute inset-0 flex items-center justify-center bg-background/60 transition-opacity"
        :class="{'opacity-0 group-hover:opacity-100': !isTouchLayout}"
      >
        <Button size="sm" class="shadow-md" type="button" @click.stop="emit('join', project)">
          <UserRoundPlus class="w-3.5 h-3.5 mr-1" />
          Join
        </Button>
      </div>

      <Button
        v-if="!joinable"
        variant="secondary"
        class="absolute top-1.5 left-1.5 w-8 h-8 p-0 rounded-full shadow-md bg-background hover:bg-secondary transition-opacity"
        :class="[
          pinned ? 'text-primary' : '',
          !isTouchLayout && !pinned ? 'opacity-0 group-hover:opacity-100' : ''
        ]"
        :aria-label="pinned ? `Unpin ${project.project_name}` : `Pin ${project.project_name}`"
        :aria-pressed="pinned"
        :title="pinned ? 'Unpin project' : 'Pin project'"
        type="button"
        @click.stop="togglePin(project.project_id)"
      >
        <Pin class="w-3.5 h-3.5" :class="pinned ? 'fill-current' : ''" />
      </Button>

      <div
        v-if="!joinable"
        class="absolute top-1.5 right-1.5 transition-opacity"
        :class="{'opacity-0 group-hover:opacity-100': !isTouchLayout}"
        @click.stop
      >
        <ProjectActionsMenu :project="project">
          <template #trigger>
            <Button
              variant="secondary"
              class="w-8 h-8 p-0 rounded-full shadow-md bg-background hover:bg-secondary"
              :aria-label="`Actions for ${project.project_name}`"
              type="button"
            >
              <Ellipsis class="w-3.5 h-3.5" />
            </Button>
          </template>
        </ProjectActionsMenu>
      </div>
    </div>

    <div class="mt-2 text-sm font-medium truncate" :title="project.project_name">{{ project.project_name }}</div>
    <div v-if="parentName" class="text-xs text-muted-foreground truncate">{{ parentName }}</div>
    <div v-if="lastActivity" class="text-xs text-muted-foreground truncate">
      Last activity {{ lastActivity }}
    </div>
  </div>
</template>
